import React, { useState } from 'react';
import Tooltip from './ui/Tooltip';

interface EnvironmentalImpactCardProps {
  distanceKm: number;
  co2Kg: number;
  formattedDistance?: string;
  className?: string;
}

const formatDistanceDefault = (value: number) => `${Math.round(value)} km`;

const tooltipContent = (
  <div>
    🚨 TOTAL AVIATION-CAUSED CLIMATE HEATING IS <b>3 TIMES</b> THAT OF CO₂ ALONE.
    <br />
    <br />
    Flight comparisons are estimates that include radiative forcing effects that occur when emissions
    happen at high altitude, which amplify climate impact by ~3x compared to ground-level CO₂
    emissions.
    <br />
    <br />
    Emissions including non-CO₂ effects (using an RFI factor to account for contrails, NOₓ-ozone,
    cirrus, etc.): ≈ 173 g CO₂e per pax·km (high level estimate)
    <br />
    <br />
    Learn more{' '}
    <a
      href="https://stay-grounded.org/wp-content/uploads/2020/10/SG_Factsheet_Non-CO2_2020.pdf"
      target="_blank"
      rel="noreferrer"
      className="underline"
    >
      here
    </a>{' '}
    and{' '}
    <a
      href="https://ourworldindata.org/travel-carbon-footprint"
      target="_blank"
      rel="noreferrer"
      className="underline"
    >
      here
    </a>
    .
  </div>
);

const EnvironmentalImpactCard: React.FC<EnvironmentalImpactCardProps> = ({
  distanceKm,
  co2Kg,
  formattedDistance,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!distanceKm || distanceKm <= 0) {
    return null;
  }

  const calculatedPercent = Math.round((co2Kg / (distanceKm * 0.173)) * 100);
  const environmentalImpactPercent = Number.isFinite(calculatedPercent) ? Math.max(calculatedPercent, 0) : 0;

  return (
    <div className={`rounded-md border border-[var(--border)] bg-emerald-50 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="font-semibold text-[var(--text)]">🌍 Environmental Impact</div>
        <button
          onClick={() => setIsExpanded(prev => !prev)}
          className="px-2 py-0.5 text-xs font-medium rounded bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--border)] transition-colors duration-200 min-w-[48px] min-h-[20px]"
          style={{ touchAction: 'manipulation' }}
        >
          {isExpanded ? 'hide' : 'show'}
        </button>
      </div>
      {isExpanded && (
        <div className="text-[var(--text)] text-sm leading-6">
          The{' '}
          <Tooltip content={tooltipContent} widthClassName="w-72">
            climate impact
          </Tooltip>{' '}
          of this journey is approximately{' '}
          <span className="font-semibold">{environmentalImpactPercent}% of flying in economy class</span>{' '}
          to go through the same distance ({formattedDistance ?? formatDistanceDefault(distanceKm)}).
        </div>
      )}
    </div>
  );
};

export default EnvironmentalImpactCard;


